/**
 * React Hook for SEO Meta Tags
 *
 * Quick Win for FRONTEND-006: AIALGO Frontend Metrics Optimization
 * Priority: High
 *
 * Automatically applies meta tags when route changes
 */

import { useEffect } from 'react'
import { useLocation } from '@tanstack/react-router'
import {
  generatePageMetaTags,
  applyMetaTags,
  PREDEFINED_META,
  type PageMetaOptions,
} from '@/lib/seo-meta'

/**
 * Hook to apply SEO meta tags to a page
 *
 * @param options - Page meta options
 * @param options.url - Current page URL (optional, will use current route if not provided)
 * @param options.title - Page title
 * @param options.description - Page description
 * @param options.keywords - Array of keywords
 * @param options.image - Open Graph image URL
 * @param options.canonical - Canonical URL (optional, will be generated)
 * @param options.noindex - Add noindex robots directive
 * @param options.nofollow - Add nofollow robots directive
 * @param options.type - Page type for Open Graph (website, article, product)
 * @param options.structuredData - JSON-LD structured data object
 *
 * @example
 * ```tsx
 * useSEOMeta({
 *   title: 'My Algorithm - Trading Bot',
 *   description: 'A powerful trading algorithm for automated trading',
 *   keywords: ['algorithmic trading', 'trading bot', 'AI trading'],
 *   type: 'product'
 * })
 * ```
 */
export function useSEOMeta(options: PageMetaOptions & { url?: string }) {
  const location = useLocation()

  useEffect(() => {
    const url = options.url || location.pathname
    const meta = generatePageMetaTags({
      ...options,
      url,
    })

    applyMetaTags(meta)

    // Add structured data if provided
    if (options.structuredData && typeof document !== 'undefined') {
      const scriptId = 'structured-data-json-ld'
      let scriptElement = document.getElementById(scriptId) as HTMLScriptElement | null

      if (!scriptElement) {
        scriptElement = document.createElement('script')
        scriptElement.id = scriptId
        scriptElement.type = 'application/ld+json'
        document.head.appendChild(scriptElement)
      }

      scriptElement.textContent = JSON.stringify(options.structuredData)
    }
  }, [location.pathname, JSON.stringify(options)])
}

/**
 * Hook for predefined page meta tags
 *
 * @param pageKey - Key from PREDEFINED_META
 * @param overrides - Optional override properties
 *
 * @example
 * ```tsx
 * usePredefinedSEOMeta('marketplace', {
 *   title: 'Custom Marketplace Title'
 * })
 * ```
 */
export function usePredefinedSEOMeta(
  pageKey: keyof typeof PREDEFINED_META,
  overrides?: Partial<PageMetaOptions>
) {
  const baseMeta = PREDEFINED_META[pageKey]

  useEffect(() => {
    const meta = generatePageMetaTags({
      ...baseMeta,
      ...overrides,
    })

    applyMetaTags(meta)
  }, [])
}

/**
 * Hook for algorithm/product page meta tags
 *
 * @param algorithm - Algorithm data object
 *
 * @example
 * ```tsx
 * useAlgorithmSEOMeta(algorithm)
 * ```
 */
export function useAlgorithmSEOMeta(algorithm: {
  name: string
  description: string
  fullDescription?: string
  category?: { name: string }
  rating?: number
  reviewsCount?: number
  slug: string
  pricingType?: string
  price?: number
}) {
  useEffect(() => {
    const url = `/algorithm/${algorithm.slug}`
    const meta = generatePageMetaTags({
      title: algorithm.name,
      description: algorithm.fullDescription || algorithm.description,
      keywords: [
        algorithm.name,
        'algorithmic trading',
        'trading algorithm',
        algorithm.category?.name || '',
        'automated trading',
      ].filter(Boolean),
      url,
      type: 'product',
    })

    applyMetaTags(meta)

    // Add structured data for product
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: algorithm.name,
      description: algorithm.fullDescription || algorithm.description,
      category: algorithm.category?.name,
      offers: algorithm.pricingType === 'free' ? {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      } : algorithm.price ? {
        '@type': 'Offer',
        price: algorithm.price.toString(),
        priceCurrency: 'USD',
      } : undefined,
      aggregateRating: algorithm.rating ? {
        '@type': 'AggregateRating',
        ratingValue: algorithm.rating,
        reviewCount: algorithm.reviewsCount || 0,
      } : undefined,
    }

    const scriptId = 'structured-data-json-ld'
    let scriptElement = document.getElementById(scriptId) as HTMLScriptElement | null

    if (!scriptElement) {
      scriptElement = document.createElement('script')
      scriptElement.id = scriptId
      scriptElement.type = 'application/ld+json'
      document.head.appendChild(scriptElement)
    }

    scriptElement.textContent = JSON.stringify(structuredData)
  }, [algorithm.slug, algorithm.name, algorithm.description])
}

/**
 * Hook for article/content page meta tags
 *
 * @param article - Article/content data object
 *
 * @example
 * ```tsx
 * useArticleSEOMeta(article)
 * ```
 */
export function useArticleSEOMeta(article: {
  title: string
  description: string
  author?: string
  publishedAt?: string
  updatedAt?: string
  slug: string
  category?: string
  tags?: string[]
  coverImage?: string
}) {
  useEffect(() => {
    const url = `/content/${article.slug}`
    const meta = generatePageMetaTags({
      title: article.title,
      description: article.description,
      keywords: article.tags || [article.category || ''],
      url,
      type: 'article',
      image: article.coverImage,
    })

    applyMetaTags(meta)

    // Add structured data for article
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.description,
      author: {
        '@type': 'Person',
        name: article.author || 'AIAlgo Team',
      },
      datePublished: article.publishedAt,
      dateModified: article.updatedAt || article.publishedAt,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
    }

    const scriptId = 'structured-data-json-ld'
    let scriptElement = document.getElementById(scriptId) as HTMLScriptElement | null

    if (!scriptElement) {
      scriptElement = document.createElement('script')
      scriptElement.id = scriptId
      scriptElement.type = 'application/ld+json'
      document.head.appendChild(scriptElement)
    }

    scriptElement.textContent = JSON.stringify(structuredData)
  }, [article.slug, article.title])
}
